import { useRef, useState } from "react";
import { TbPencil } from "react-icons/tb";
import { FaChevronLeft } from "react-icons/fa";
import Button from "@/components/ui/Button";

export default function Toolbar({
  name,
  onNameChange,
  onSave,
  onClose,
  saving,
  hasUnsavedChanges,
}) {
  const [editingName, setEditingName] = useState(false);
  const [draftName, setDraftName] = useState(name);
  const renameCommittedRef = useRef(false);

  const startRenaming = () => {
    renameCommittedRef.current = false;
    setDraftName(name);
    setEditingName(true);
  };

  // Enter commits, Escape/blur cancels — renameCommittedRef lets the Enter
  // handler close the input without the blur it triggers re-firing a cancel
  // right behind it (same pattern as HierarchyPanel's row rename).
  const finishRenaming = (commit) => {
    renameCommittedRef.current = true;
    if (commit) {
      const trimmed = draftName.trim();
      if (trimmed && trimmed !== name) onNameChange(trimmed);
    }
    setEditingName(false);
  };

  return (
    // Sized/spaced to match AssetsEditor's own header row (index.jsx) — same
    // fixed h-20, same @[700px]:px-10/px-2 responsive padding, no border or
    // background tint — so the two editors read as one consistent page
    // chrome rather than a distinct "embedded tool" look.
    <div className="flex items-center justify-between px-2 h-20 flex-shrink-0 border-b">
      <div className="flex items-center gap-2">
        <button
          type="button"
          title="Close"
          onClick={onClose}
          className="text-secondary-foreground hover:text-white cursor-pointer w-8 h-8 flex justify-center items-center rounded-md">
          <FaChevronLeft className="w-4 h-4" />
        </button>
        {editingName ? (
          <input
            id="Scene3DToolbar__nameInput"
            autoFocus
            onFocus={(e) => e.target.select()}
            value={draftName}
            onChange={(e) => setDraftName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                finishRenaming(true);
              } else if (e.key === "Escape") {
                e.preventDefault();
                finishRenaming(false);
              }
            }}
            onBlur={() => {
              if (!renameCommittedRef.current) finishRenaming(false);
            }}
            placeholder="Scene name"
            className="text-2xl font-bold border-l-4 border-primary pl-4 pr-2 min-w-32 bg-transparent outline-none rounded-sm focus:bg-secondary/10"
          />
        ) : (
          <div className="flex gap-2 group">
            <h1 className="text-2xl font-bold border-l-4 border-primary pl-4">
              {name}
            </h1>
            <button
              id="Scene3DToolbar__renameTrigger"
              type="button"
              title="Rename"
              onClick={startRenaming}
              className="group-hover:flex hidden text-secondary-foreground hover:text-white cursor-pointer w-8 h-8 justify-center items-center rounded-md">
              <TbPencil className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

      <div className="flex items-center gap-2">
        <Button
          type="primary"
          onClick={onSave}
          disabled={saving || !hasUnsavedChanges}
          className="p-2 text-xs min-w-22"
          title="Save (⌘S / Ctrl+S)">
          {saving ? "Saving..." : "Save"}
        </Button>
      </div>
    </div>
  );
}
